import { useRequest, usePagination, useMemoizedFn } from 'ahooks';
import { message } from 'antd';
import { useSetReturnCancelCount } from 'hooks/business/openReturnStatus';
import { useCallback, useMemo, useState } from 'react';
import { useTranslate } from '@tolgee/react';
import {
  queryReturnListV2,
  ProcessStatus,
  queryReturnListStateCountV2,
  getTalkInfoByBuyerStore,
  SecondaryProcessStatus,
  ReturnOrderListItemV2,
} from 'services/api/refund';
import toLowerCase, { toUpperCase } from 'utils/toLowerCase';
import type { FilterState } from './useFilter';
import useGetStoreIdList from './useGetStoreIdList';
import dayjs from 'dayjs';
import { logisticsList } from 'services/api/conversation';
import { isTiKTokUpperCase } from 'lib/utils/isTikTok';

export type StatusFilter = ProcessStatus | 'ALL';

export const useGetReturnListParams = () => {
  const getStoreIdList = useGetStoreIdList();

  return useMemoizedFn(
    async (
      query: FilterState,
      statusFilter: StatusFilter,
      secondaryProcessStatus: SecondaryProcessStatus,
      platform: General.PlatformUpperCase,
    ) => {
      const {
        startDate,
        endDate,
        follower,
        region,
        storeList,
        dueTimeEnd,
        mercadoDueTimeEnd,
        orderSn,
        ...restQuery
      } = query;
      const internalStoreIdList = await getStoreIdList({ region, follower, storeList }, platform);

      return {
        ...restQuery,
        orderSn: orderSn?.trim(),
        processStatus: statusFilter === 'ALL' ? undefined : statusFilter,
        applicationStart: startDate,
        applicationEnd: endDate,
        platform: toLowerCase(platform),
        region,
        internalStoreIdList,
        secondaryProcessStatus:
          ((platform === 'SHOPEE' && statusFilter === 'PENDING') ||
            (isTiKTokUpperCase(platform) &&
              (statusFilter === 'PENDING' || statusFilter === 'PROCESS'))) &&
          secondaryProcessStatus !== SecondaryProcessStatus.ALL
            ? secondaryProcessStatus
            : undefined,
        dueTimeEnd:
          platform === 'KWAI' && statusFilter === 'PENDING' && dueTimeEnd
            ? dayjs().add(1, 'days').valueOf().toString()
            : platform === 'MERCADO' && mercadoDueTimeEnd
            ? mercadoDueTimeEnd.type === 'hours'
              ? dayjs().add(mercadoDueTimeEnd.hours, 'hours').valueOf().toString()
              : mercadoDueTimeEnd.day.valueOf().toString()
            : '0',
      };
    },
  );
};

const useReturnList = (
  query: FilterState,
  platform: General.PlatformUpperCase,
  defaultStatusFilter: StatusFilter = 'PENDING',
) => {
  const [statusFilter, setStatusFilter] = useState<StatusFilter>(defaultStatusFilter);
  const [secondaryProcessStatus, setSecondaryProcessStatus] = useState<SecondaryProcessStatus>(
    SecondaryProcessStatus.ALL,
  );
  const { t } = useTranslate();
  const refreshCount = useSetReturnCancelCount();
  const getStoreIdList = useGetStoreIdList();

  const getReturnListParams = useGetReturnListParams();

  const {
    data,
    pagination,
    loading,
    refresh: refreshList,
  } = usePagination(
    async ({ pageSize, current }) => {
      try {
        const params = await getReturnListParams(
          query,
          statusFilter,
          secondaryProcessStatus,
          platform,
        );
        const fetch = (
          current: number,
        ): Promise<{
          total: number;
          returnOrderDataList: (ReturnOrderListItemV2 & {
            logisticsList?: Conversation.Trade.Logistics[];
            reverseLogisticsList?: Conversation.Trade.ReverseLogistics[];
          })[];
        }> =>
          queryReturnListV2({
            ...params,
            page: {
              pageNo: current,
              pageSize,
            },
          }).then(({ returnOrderDataList, total }) => {
            if (returnOrderDataList.length === 0 && total === 0 && current > 1) {
              return fetch(current - 1);
            }
            return {
              returnOrderDataList,
              total,
            };
          });
        const { returnOrderDataList, total } = await fetch(current);
        refreshCount();
        return {
          list: returnOrderDataList,
          total,
        };
      } catch (error: any) {
        message.error(t('scene.orderWorkspace.returnRefundTab.error.fetchListFailed'));
      }
      return {
        list: [],
        total: 0,
      };
    },
    {
      refreshDeps: [query, statusFilter, platform, secondaryProcessStatus],
    },
  );

  const { data: logisticsData } = useRequest(
    () => {
      return logisticsList({
        internal_trade_ids: data?.list.map((_) => _.internalTradeId).join(',') || '',
        needReverse: true,
      });
    },
    {
      ready: platform === 'SHOPEE' && Boolean(data?.list.length),
      refreshDeps: [data],
      onError: () => message.error(t('scene.orderWorkspace.returnRefundTab.error.fetchListFailed')),
    },
  );

  const list = useMemo(() => {
    return data?.list.map((data) => {
      const logisticsList = logisticsData?.data.data.filter(
        ({ internalTradeId }) => `${internalTradeId}` === `${data.internalTradeId}`,
      );
      const reverseLogisticsList = logisticsData?.data.reverseData?.filter(
        ({ internalTradeId }) => `${internalTradeId}` === `${data.internalTradeId}`,
      );
      if (!logisticsList?.length && !reverseLogisticsList?.length) {
        return data;
      }
      return {
        ...data,
        logisticsList,
        reverseLogisticsList,
      };
    });
  }, [data, logisticsData]);

  const { data: conversationList } = useRequest(
    async () => {
      if (!data || !data.list) {
        return [];
      }
      try {
        const items = data.list.map((x) => {
          const { region, externalBuyerId, externalStoreId } = x;
          return {
            channel: platform,
            region: toUpperCase(region),
            externalBuyerId,
            externalStoreId,
          };
        });
        const { talkInfos } = await getTalkInfoByBuyerStore({ items, needPhone: true });
        return talkInfos;
      } catch (error) {
        return [];
      }
    },
    {
      refreshDeps: [data],
    },
  );
  const { data: statusCount, refresh: refreshTabCount } = useRequest(
    async () => {
      const {
        startDate,
        endDate,
        region,
        follower,
        storeList,
        dueTimeEnd,
        mercadoDueTimeEnd,
        ...restQuery
      } = query;
      const internalStoreIdList = await getStoreIdList({ region, follower, storeList }, platform);
      return queryReturnListStateCountV2({
        ...restQuery,
        applicationStart: startDate,
        applicationEnd: endDate,
        platform: toLowerCase(platform),
        region,
        internalStoreIdList,
        dueTimeEnd:
          platform === 'KWAI' && statusFilter === 'PENDING' && dueTimeEnd
            ? dayjs().add(1, 'days').valueOf().toString()
            : platform === 'MERCADO' && mercadoDueTimeEnd
            ? mercadoDueTimeEnd.type === 'hours'
              ? dayjs().add(mercadoDueTimeEnd.hours, 'hours').valueOf().toString()
              : mercadoDueTimeEnd.day.valueOf().toString()
            : '0',
      });
    },
    {
      refreshDeps: [query, statusFilter, platform],
    },
  );
  const refresh = useCallback(() => {
    refreshList();
    refreshTabCount();
  }, [refreshList, refreshTabCount]);
  return {
    list,
    setStatusFilter,
    statusFilter,
    pagination,
    loading,
    statusCount,
    refresh,
    conversationList,
    setSecondaryProcessStatus,
    secondaryProcessStatus,
  };
};

export default useReturnList;
